import type { CSSProperties, ReactNode } from 'react'

/** The two button sizes used across the app. */
const sizes = {
  regular: 'px-3.5 py-2.5 text-base/6 sm:px-3 sm:py-2 sm:text-sm/5',
  small: 'px-3 py-1.5 text-base/6 sm:px-2.5 sm:py-1 sm:text-sm/5',
}

/** Doubloon gold: at most one per screen. */
export const primaryButton =
  'inline-flex items-center justify-center gap-x-1.5 rounded-lg bg-brand-400 font-semibold text-zinc-950 hover:bg-brand-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50'

export const softButton =
  'inline-flex items-center justify-center gap-x-1.5 rounded-lg bg-zinc-950/5 font-semibold text-zinc-950 hover:bg-zinc-950/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:bg-white/10 dark:text-white dark:hover:bg-white/15'

export const plainButton =
  'inline-flex items-center justify-center gap-x-1.5 rounded-lg font-medium text-zinc-600 hover:bg-zinc-950/5 hover:text-zinc-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white'

export function buttonClass(style: 'primary' | 'soft' | 'plain', size: keyof typeof sizes = 'regular') {
  const base = style === 'primary' ? primaryButton : style === 'soft' ? softButton : plainButton
  return `${base} ${sizes[size]}`
}

/** Rank titles from the server map to a chess glyph. */
const RANK_GLYPHS: Record<string, string> = {
  Pawn: '♟', Knight: '♞', Bishop: '♝', Rook: '♜', Queen: '♛', King: '♚', Grandmaster: '♚',
}

export function rankGlyph(rank: string) {
  return `${RANK_GLYPHS[rank] ?? '♟'}︎`
}

export function XPBar({ progress, className = '' }: { progress: number; className?: string }) {
  const percent = `${Math.round(Math.min(Math.max(progress, 0), 1) * 100)}%`
  return (
    <div className={`h-2 overflow-hidden rounded-full bg-zinc-950/5 dark:bg-white/10 ${className}`}>
      <div className="h-full w-(--progress) rounded-full bg-linear-to-r from-brand-500 to-brand-300 transition-[width] duration-500" style={{ '--progress': percent } as CSSProperties} />
    </div>
  )
}

/** A radio group drawn as a segmented control. */
export function Segmented<T extends string | number>({
  label,
  name,
  value,
  options,
  onChange,
}: {
  label: string
  name: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex overflow-x-auto rounded-lg bg-zinc-950/5 p-0.5 dark:bg-white/10">
      {options.map((option) => (
        <label
          key={String(option.value)}
          className="flex-1 cursor-pointer rounded-md px-3 py-2 text-center text-base/6 font-medium whitespace-nowrap text-zinc-600 has-checked:bg-white has-checked:text-zinc-950 has-checked:shadow-xs has-checked:ring-1 has-checked:ring-black/5 has-focus-visible:outline-2 has-focus-visible:outline-brand-500 sm:py-1 sm:text-sm/6 dark:text-zinc-400 dark:has-checked:bg-white/15 dark:has-checked:text-white dark:has-checked:shadow-none dark:has-checked:ring-0"
        >
          <input
            type="radio"
            name={name}
            value={String(option.value)}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
            className="sr-only"
          />
          {option.label}
        </label>
      ))}
    </div>
  )
}

export function SideBadge({ side, children }: { side: 'white' | 'black'; children: ReactNode }) {
  return (
    <div
      className={`inline-flex items-center gap-x-2 rounded-full py-1 pr-3 pl-2 text-sm/6 font-semibold ring-1 ${side === 'white' ? 'bg-white text-zinc-950 ring-zinc-950/15' : 'bg-zinc-950 text-white ring-zinc-950 dark:ring-white/15'}`}
    >
      <div className="font-chess text-base leading-none">{side === 'white' ? '♔︎' : '♚︎'}</div>
      {children}
    </div>
  )
}
